/**
 * api/assistant.js — chat do Assistente com streaming (SSE sobre fetch).
 *
 * EventSource é GET-only e não manda header de auth; então lemos o SSE de
 * POST /assistant/stream direto do ReadableStream (api.stream injeta o Bearer).
 * Eventos: token {text} · done {finishReason,model} · error {message}.
 */

function _dispatch(rawEvent, { onToken, onDone, onError, onSources }) {
  let event = 'message';
  const dataLines = [];
  for (const line of rawEvent.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
    // linhas ": keep-alive" (comentário SSE) são ignoradas
  }
  if (dataLines.length === 0) return;
  let data = {};
  try {
    data = JSON.parse(dataLines.join('\n'));
  } catch {
    return;
  }
  if (event === 'token') onToken?.(data.text || '');
  else if (event === 'sources') onSources?.(data.files || []);
  else if (event === 'done') onDone?.(data);
  else if (event === 'error') onError?.(data.message || 'erro');
}

/**
 * Envia `messages` e chama os handlers a cada token/done/error.
 * @param {{ stream: Function }} api  — cliente de useApi()
 * @param {Array<{role:string,content:string}>} messages
 * @param {{ tier?:string, signal?:AbortSignal,
 *           onToken?:(t:string)=>void, onDone?:(d:object)=>void,
 *           onError?:(m:string)=>void }} [handlers]
 */
export async function streamAssistant(api, messages, handlers = {}) {
  const { tier = 'haiku', projectId, signal, onToken, onDone, onError, onSources } = handlers;
  let res;
  try {
    const body = { messages, tier };
    if (projectId) body.projectId = projectId;
    res = await api.stream('/assistant/stream', body, { signal });
  } catch (e) {
    if (!signal?.aborted) onError?.(String(e?.message || e));
    return;
  }
  if (!res.ok || !res.body) {
    onError?.(`HTTP ${res.status}`);
    return;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buf.indexOf('\n\n')) !== -1) {
        _dispatch(buf.slice(0, idx), { onToken, onDone, onError, onSources });
        buf = buf.slice(idx + 2);
      }
    }
    if (buf.trim()) _dispatch(buf, { onToken, onDone, onError, onSources });
  } catch (e) {
    if (!signal?.aborted) onError?.(String(e?.message || e));
  }
}
